import axios from "axios";

import type {
	AuthUser,
	ChangePasswordPayload,
	ForgotPasswordPayload,
	LoginCredentials,
	ResetPasswordPayload,
} from "./types";

const authAxios = axios.create({
	baseURL: "/auth",
	withCredentials: true,
});

function unwrapError(error: unknown): never {
	if (axios.isAxiosError(error)) {
		const data = error.response?.data as
			| { message?: string; error?: string }
			| undefined;
		const message = data?.message ?? data?.error ?? error.message;
		throw new Error(message);
	}
	throw error;
}

export const authService = {
	async me(): Promise<AuthUser | null> {
		try {
			const { data } = await authAxios.get<AuthUser>("/me");
			return data;
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 401) {
				return null;
			}
			return unwrapError(error);
		}
	},

	async login(credentials: LoginCredentials): Promise<AuthUser> {
		try {
			const { data } = await authAxios.post<AuthUser>("/login", credentials);
			return data;
		} catch (error) {
			return unwrapError(error);
		}
	},

	async logout(): Promise<void> {
		try {
			await authAxios.post("/logout");
		} catch (error) {
			return unwrapError(error);
		}
	},

	async changePassword(payload: ChangePasswordPayload): Promise<AuthUser> {
		try {
			await authAxios.post("/change-password", payload);
			const { data } = await authAxios.get<AuthUser>("/me");
			return data;
		} catch (error) {
			return unwrapError(error);
		}
	},

	async forgotPassword(payload: ForgotPasswordPayload): Promise<void> {
		try {
			await authAxios.post("/forgot-password", payload);
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 429) {
				throw new Error("RATE_LIMITED");
			}
			return unwrapError(error);
		}
	},

	async resetPassword(payload: ResetPasswordPayload): Promise<void> {
		try {
			await authAxios.post("/reset-password", payload);
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 429) {
				throw new Error("RATE_LIMITED");
			}
			return unwrapError(error);
		}
	},
};
